'use client';
import dynamic from 'next/dynamic';
import useBeforeUnload from '@/hooks/useBeforeUnload';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { QueryClientProvider } from '@tanstack/react-query';
import { SessionProvider } from 'next-auth/react';
import { getQueryClient } from '@/queryClient';
import BackendService from '@/services/backendService';
import { configureConfig } from '@/config';
import ErrorBoundary from '@/components/ErrorBoundary';
import { useDetectBrowserTranslation } from '@/hooks/useDetectBrowserTranslation';
import { Telemetry } from '@/hooks/useTelemetry';
import { maybeResetForCuripodEmbed } from '@/utils/embedReset';

const GameButler = dynamic(() => import('../components/GameButler'), {
  ssr: false,
});

const App = () => {
  useBeforeUnload();
  const { config } = configureConfig();
  // Synchronously (before the lazy GameButler mounts and reads the cached
  // participant) wipe Curipod-owned state when embedded in a Curipod iframe, so
  // every simulator reload starts fresh instead of reusing a dead session.
  useState(() => {
    maybeResetForCuripodEmbed();
    return null;
  });
  useEffect(() => {
    const LocalstorageService = require('@/LocalstorageService').default;
    const IndexedDBStorageService =
      require('@/IndexedDBStorageService').default;

    // Remove any old curipod localstorage cache
    const clearStaleCache = async () => {
      LocalstorageService.clearStaleCache();
      await IndexedDBStorageService.clearStaleCache();
    };
    clearStaleCache();
  }, []);
  const { browserTranslation } = useDetectBrowserTranslation();
  useEffect(() => {
    if (browserTranslation) {
      Telemetry()?.setCustomAttribute('browserTranslation', browserTranslation);
    }
  }, [browserTranslation]);

  useState(() => {
    const bService = new BackendService();
    bService.init(config.backend.coreApiUrl);

    return bService;
  });
  const queryClient = getQueryClient();
  return (
    <ErrorBoundary>
      {/* Student SSO identity. Carries no Curipod tokens — the participant JWT
          comes from /v0.5/live/join and lives in HttpService, so there is nothing
          here to keep refreshed. Window-focus refetch is off to avoid a session
          request every time a student tabs back into the lesson. */}
      <SessionProvider refetchOnWindowFocus={false}>
        <QueryClientProvider client={queryClient}>
          <AppContainer>
            <GameButler />
          </AppContainer>
        </QueryClientProvider>
      </SessionProvider>
    </ErrorBoundary>
  );
};

const AppContainer = styled.div`
  height: 100%;
  width: 100%;
`;

export default App;
